import React, { useState } from "react";

import {
  faClose,
  faFileImport,
  faInfoCircle,
  faKey,
  faPlus,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { Controller, useForm } from "react-hook-form";

import {
  useCreateOrganizationAuthSource,
  useDeleteOrganizationAuthSource,
  useMigrateUsersAuthSource,
  useOrganizationAuthSources,
} from "src/api/taktile/queries";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { Input } from "src/base-components/Input";
import { LoadingView } from "src/base-components/LoadingView";
import { Select } from "src/base-components/Select";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import {
  OrganizationAuthSourceCreateNamed,
  OrganizationAuthSourceResponse,
  TaktileApiSchemasOrganizationAvailableAuthSources,
} from "src/clients/taktile-api";
import { EmptyState } from "src/design-system/EmptyState";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon, IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";

const columnHelper = createColumnHelper<OrganizationAuthSourceResponse>();

const Actions: React.FC<{
  orgId: string;
  authSourceId: string;
  authSource: OrganizationAuthSourceResponse;
  availableAuthSources: OrganizationAuthSourceResponse[];
}> = ({ orgId, authSourceId, authSource, availableAuthSources }) => {
  const { mutateAsync: deleteAuthSource } = useDeleteOrganizationAuthSource(
    orgId,
    authSourceId,
  );
  const { mutateAsync: migrateAuthSource } = useMigrateUsersAuthSource(orgId);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationError, setMigrationError] = useState<string | null>(null);

  const handleDelete = () => {
    setShowConfirmation(false);
    deleteAuthSource();
  };

  const handleMigration = async (data: { domain: string }) => {
    setIsMigrating(true);
    setMigrationError(null);

    try {
      const result = await migrateAuthSource({
        authSourceId: authSource.user_auth_source_id,
        domain: data.domain,
      });

      setShowMigrationModal(false);

      // Show success toast with migration result
      toastActions.success({
        title: `Successfully migrated ${result.affected_users_count} user${result.affected_users_count !== 1 ? "s" : ""} to ${result.new_source_name}`,
        description: `Users with @${data.domain} email addresses have been moved to the ${result.new_source_name} authentication source.`,
      });
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { status: number; data?: { detail?: string } };
      };

      if (axiosError?.response?.status === 400) {
        setMigrationError(
          "Invalid request. Please check your configuration and try again.",
        );
      } else if (axiosError?.response?.status === 404) {
        setMigrationError(
          "Auth source not found. Please refresh the page and try again.",
        );
      } else if (
        axiosError?.response?.status &&
        axiosError.response.status >= 500
      ) {
        setMigrationError(
          axiosError.response?.data?.detail ||
            "Failed to migrate users. Please contact support.",
        );
      } else {
        setMigrationError(
          axiosError.response?.data?.detail || "An unknown error occurred.",
        );
      }
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <div className="flex flex-row-reverse gap-2">
      <IconButton
        icon={faClose}
        size="xs"
        onClick={() => setShowConfirmation(true)}
      />
      <Tooltip title="Move users with a specific domain to this authentication source">
        <IconButton
          icon={faFileImport}
          size="xs"
          onClick={() => setShowMigrationModal(true)}
        />
      </Tooltip>

      <ConfirmationModal
        open={showConfirmation}
        title="Are you sure you want to delete this auth source?"
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleDelete}
      />

      <MigrateAuthSourceModal
        availableAuthSources={availableAuthSources}
        currentAuthSource={authSource}
        error={migrationError}
        isSubmitting={isMigrating}
        open={showMigrationModal}
        onClose={() => setShowMigrationModal(false)}
        onConfirm={handleMigration}
      />
    </div>
  );
};

type AuthProviderFormData = {
  source_name: TaktileApiSchemasOrganizationAvailableAuthSources;
  domain_name: string;
  well_known_url?: string;
  client_id?: string;
  client_secret?: string;
};

const AddAuthProviderModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: (authSource: OrganizationAuthSourceCreateNamed) => Promise<void>;
  isSubmitting?: boolean;
  error?: string | null;
}> = ({ open, onClose, onConfirm, isSubmitting = false, error = null }) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm<AuthProviderFormData>({
    mode: "onChange",
    defaultValues: {
      source_name: TaktileApiSchemasOrganizationAvailableAuthSources.GOOGLE,
      domain_name: "",
      well_known_url: "",
      client_id: "",
      client_secret: "",
    },
  });

  const watchedSourceName = watch("source_name");

  const validateUrl = (url: string): boolean => {
    if (!url) return true; // Allow empty for non-required fields
    try {
      new URL(url);
      return url.includes("well-known");
    } catch {
      return false;
    }
  };

  const validateUrlWhitespace = (url: string): string | true => {
    if (!url) return true; // Allow empty for non-required fields
    if (url !== url.trim()) {
      return "URL contains leading or trailing whitespaces";
    }
    return true;
  };

  const validateDomain = (domain: string): boolean => {
    if (!domain || !domain.includes(".")) return false;
    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return domainRegex.test(domain);
  };

  const getProviderDescription = (provider: string): string => {
    switch (provider) {
      case "google":
        return "Allow users to sign in with their Google accounts";
      case "microsoft":
        return "Allow users to sign in with their Microsoft accounts";
      case "okta":
        return "Configure custom Okta/OpenID Connect SSO integration";
      default:
        return "";
    }
  };

  const onSubmit = async (data: AuthProviderFormData) => {
    const authSource: OrganizationAuthSourceCreateNamed = {
      source_name: data.source_name,
      domain_name: data.domain_name,
    };

    if (data.source_name === "okta" && data.well_known_url) {
      authSource.client_id = data.client_id;
      authSource.client_secret = data.client_secret;
      authSource.well_known_url = data.well_known_url;
    }

    await onConfirm(authSource);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ConfirmationModal
      confirmationButtonText={isSubmitting ? "Adding..." : "Add Provider"}
      disableConfirmationButton={!isValid || isSubmitting}
      open={open}
      size="lg"
      title="Add Authentication Provider"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
    >
      <div className="space-y-3">
        <p className="mb-2 text-gray-600 text-sm">
          Configure an authentication provider to allow users to sign in to your
          organization.
        </p>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="flex items-center gap-2">
              <Icon variant="error" icon={faWarning} size="sm" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex flex-col">
            <label className="mb-1 text-left font-medium text-gray-700">
              Authentication Provider
            </label>
            <Controller
              control={control}
              name="source_name"
              render={({ field }) => (
                <Select
                  {...field}
                  options={[
                    { key: "google", value: "Google" },
                    { key: "microsoft", value: "Microsoft" },
                    { key: "okta", value: "Okta/OpenID Connect" },
                  ]}
                />
              )}
            />
            <p className="mt-1 font-inter-normal-12px text-gray-500">
              {getProviderDescription(watchedSourceName)}
            </p>
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-left font-medium text-gray-700">
              Organization Domain <span className="text-red-500">*</span>
            </label>
            <Input
              errored={!!errors.domain_name}
              placeholder="e.g., company.com"
              suffixIcon={errors.domain_name ? { icon: faWarning } : undefined}
              fullWidth
              {...register("domain_name", {
                required: "Organization domain is required",
                validate: (value) => {
                  if (!validateDomain(value)) {
                    return "Please enter a valid domain name";
                  }
                  return true;
                },
              })}
            />
            {errors.domain_name && (
              <ErrorHint>{errors.domain_name.message}</ErrorHint>
            )}
            <p className="mt-1 font-inter-normal-12px text-gray-500">
              Users with this email domain will be able to sign in
            </p>
          </div>

          {watchedSourceName === "okta" && (
            <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="mb-1 flex items-center gap-2">
                <Icon variant="active" icon={faInfoCircle} size="sm" />
                <span className="font-medium text-blue-800 text-sm">
                  Okta Configuration
                </span>
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-left font-medium text-gray-700">
                  Well-Known URL <span className="text-red-500">*</span>
                </label>
                <Input
                  errored={!!errors.well_known_url}
                  placeholder="https://your-domain.okta.com/.well-known/openid_configuration"
                  suffixIcon={
                    errors.well_known_url ? { icon: faWarning } : undefined
                  }
                  fullWidth
                  {...register("well_known_url", {
                    shouldUnregister: true,
                    required:
                      watchedSourceName === "okta"
                        ? "Well-known URL is required for Okta"
                        : false,
                    validate: (value, formValues) => {
                      if (formValues.source_name === "okta" && value) {
                        // Check for whitespace first
                        const whitespaceCheck = validateUrlWhitespace(value);
                        if (whitespaceCheck !== true) {
                          return whitespaceCheck;
                        }
                        // Then check URL validity
                        if (!validateUrl(value)) {
                          return "Please enter a valid URL (must include well-known)";
                        }
                      }
                      return true;
                    },
                  })}
                />
                {errors.well_known_url && (
                  <ErrorHint>{errors.well_known_url.message}</ErrorHint>
                )}
                <p className="mt-1 font-inter-normal-12px text-gray-500">
                  Your Okta OpenID Connect discovery endpoint
                </p>
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-left font-medium text-gray-700">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <Input
                  errored={!!errors.client_id}
                  placeholder="Enter your Okta application client ID"
                  suffixIcon={
                    errors.client_id ? { icon: faWarning } : undefined
                  }
                  fullWidth
                  monospaced
                  {...register("client_id", {
                    shouldUnregister: true,
                    required:
                      watchedSourceName === "okta"
                        ? "Client ID is required for Okta"
                        : false,
                  })}
                />
                {errors.client_id && (
                  <ErrorHint>{errors.client_id.message}</ErrorHint>
                )}
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-left font-medium text-gray-700">
                  Client Secret <span className="text-red-500">*</span>
                </label>
                <Input
                  errored={!!errors.client_secret}
                  placeholder="Enter your Okta application client secret"
                  suffixIcon={
                    errors.client_secret ? { icon: faWarning } : undefined
                  }
                  type="password"
                  fullWidth
                  monospaced
                  {...register("client_secret", {
                    shouldUnregister: true,
                    required:
                      watchedSourceName === "okta"
                        ? "Client secret is required for Okta"
                        : false,
                  })}
                />
                {errors.client_secret && (
                  <ErrorHint>{errors.client_secret.message}</ErrorHint>
                )}
              </div>
            </div>
          )}
        </div>

        {!isValid && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-2">
            <p className="text-red-700 text-sm">
              Please fill in all required fields correctly to continue.
            </p>
          </div>
        )}
      </div>
    </ConfirmationModal>
  );
};

const AddAuthProvider: React.FC<{
  onOpenModal: () => void;
}> = ({ onOpenModal }) => {
  return (
    <div className="flex justify-end">
      <Button
        iconLeft={faPlus}
        size="sm"
        variant="secondary"
        onClick={onOpenModal}
      >
        <span className="hidden sm:hidden md:hidden lg:flex">
          Add auth provider
        </span>
        <span className="hidden md:flex lg:hidden">Add</span>
      </Button>
    </div>
  );
};

const getColumns = (
  orgId: string,
  onOpenModal: () => void,
  availableAuthSources: OrganizationAuthSourceResponse[],
) => {
  return [
    columnHelper.accessor("user_auth_source_name", {
      header: (info) => <Header info={info}>Auth provider</Header>,
      cell: (info) => (
        <Cell info={info}>
          <span className="block max-w-90 truncate">
            {info.getValue() || "Unknown"}
          </span>
        </Cell>
      ),
    }),
    columnHelper.accessor("domain_name", {
      header: (info) => <Header info={info}>Linked domain</Header>,
      cell: (info) => (
        <Cell info={info}>
          <span className="block max-w-90 truncate">
            {info.getValue() || "Unknown"}
          </span>
        </Cell>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: (info) => (
        <Header info={info}>
          <AddAuthProvider onOpenModal={onOpenModal} />
        </Header>
      ),
      cell: (info) => {
        const authSource = info.row.original;
        if (!authSource?.id || !authSource?.organization_id) {
          return (
            <Cell info={info}>
              <span className="text-gray-400">N/A</span>
            </Cell>
          );
        }
        return (
          <Cell info={info}>
            <Actions
              authSource={authSource}
              authSourceId={authSource.id}
              availableAuthSources={availableAuthSources}
              orgId={authSource.organization_id}
            />
          </Cell>
        );
      },
      minSize: 60,
      maxSize: 60,
    }),
  ];
};

const AuthSourcesTable: React.FC<{
  orgId: string;
  authSources: OrganizationAuthSourceResponse[];
}> = ({ authSources, orgId }) => {
  const { mutateAsync: createAuthSource } =
    useCreateOrganizationAuthSource(orgId);

  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Defensive programming: ensure authSources is always an array
  const safeAuthSources = Array.isArray(authSources) ? authSources : [];

  const handleAdd = async (authSource: OrganizationAuthSourceCreateNamed) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await createAuthSource(authSource);
      setShowAddModal(false);
    } catch (err: unknown) {
      // Handle specific error cases based on the API response
      const axiosError = err as {
        response?: { status: number; data?: { detail?: string } };
      };

      if (axiosError?.response?.status === 400) {
        const errorDetail = axiosError?.response?.data?.detail || "";
        if (errorDetail.includes("Client ID and secret required")) {
          setError(
            "Client ID and Client Secret are required for Okta configuration.",
          );
        } else if (errorDetail.includes("Bad well-known URL")) {
          setError(
            "Invalid well-known URL. Please check the URL and try again.",
          );
        } else {
          setError(
            "Invalid request. Please check your configuration and try again.",
          );
        }
      } else if (axiosError?.response?.status === 404) {
        setError(
          "Organization not found. Please refresh the page and try again.",
        );
      } else if (axiosError?.response?.status === 409) {
        setError(
          axiosError?.response?.data?.detail ||
            "This domain is already registered to another organization.",
        );
      } else if (axiosError?.response?.status === 422) {
        setError("Invalid well-known URL. Please check the URL and try again.");
      } else if (
        axiosError?.response?.status &&
        axiosError.response.status >= 500
      ) {
        setError(
          "Failed to add authentication provider. ask for support from platform team.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenModal = () => {
    setError(null);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setError(null);
    setShowAddModal(false);
  };

  // Early return if orgId is not provided
  if (!orgId) {
    return (
      <Card variant="inverted">
        <Card.Content className="flex h-[228px] items-center justify-center">
          <p className="text-gray-500">Organization ID is required</p>
        </Card.Content>
      </Card>
    );
  }

  return (
    <>
      <Card variant="inverted">
        {safeAuthSources.length > 0 ? (
          <Card.Content className="text-center">
            <Table
              columns={getColumns(orgId, handleOpenModal, safeAuthSources)}
              data={safeAuthSources}
              headerPropsGetter={() => ({
                classNameOverrides: "first:relative",
              })}
              variant="compact"
              noScroll
            />
          </Card.Content>
        ) : (
          <Card.Content className="flex h-[228px] items-center justify-center">
            <EmptyState
              actionButton={{
                label: "Add auth provider",
                icon: faPlus,
                onClick: handleOpenModal,
              }}
              description="There are currently no authentication sources configured for this organization."
              headline="No Authentication Sources"
              icon={faKey}
            />
          </Card.Content>
        )}
      </Card>

      <AddAuthProviderModal
        error={error}
        isSubmitting={isSubmitting}
        open={showAddModal}
        onClose={handleCloseModal}
        onConfirm={handleAdd}
      />
    </>
  );
};

export const AuthProviders: React.FC<{ orgId: string }> = ({ orgId }) => {
  const authSourcesResult = useOrganizationAuthSources(orgId);

  const renderUpdated = (
    authSourcesResult: OrganizationAuthSourceResponse[],
  ) => {
    // Defensive programming: ensure we have valid data
    const safeAuthSources = Array.isArray(authSourcesResult)
      ? authSourcesResult
      : [];
    return <AuthSourcesTable authSources={safeAuthSources} orgId={orgId} />;
  };

  return (
    <LoadingView
      queryResult={authSourcesResult}
      renderUpdated={renderUpdated}
    />
  );
};

type MigrateAuthSourceFormData = {
  domain: string;
};

const MigrateAuthSourceModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: (data: MigrateAuthSourceFormData) => Promise<void>;
  isSubmitting?: boolean;
  error?: string | null;
  currentAuthSource: OrganizationAuthSourceResponse;
  availableAuthSources: OrganizationAuthSourceResponse[];
}> = ({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
  error = null,
  currentAuthSource,
  availableAuthSources,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<MigrateAuthSourceFormData>({
    mode: "onChange",
    defaultValues: {
      domain: currentAuthSource.domain_name,
    },
  });

  const onSubmit = async (data: MigrateAuthSourceFormData) => {
    await onConfirm(data);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Filter out the current auth source from available options
  const otherAuthSources = availableAuthSources.filter(
    (source) => source.id !== currentAuthSource.id,
  );

  if (otherAuthSources.length === 0) {
    return (
      <ConfirmationModal
        confirmationButtonText="OK"
        disableConfirmationButton={false}
        open={open}
        title="No Migration Options"
        onClose={handleClose}
        onConfirm={() => {}}
      >
        <div className="space-y-3">
          <p className="text-gray-600 text-sm">
            No other authentication sources are available for migration. You
            need to add another auth source first before you can migrate users.
          </p>
        </div>
      </ConfirmationModal>
    );
  }

  return (
    <ConfirmationModal
      confirmationButtonText={isSubmitting ? "Migrating..." : "Migrate Users"}
      disableConfirmationButton={!isValid || isSubmitting}
      open={open}
      size="lg"
      title="Migrate Users to This Auth Source"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
    >
      <div className="space-y-3">
        <p className="mb-2 text-gray-600 text-sm">
          Migrate users with a specific domain to this authentication source.
          This will update the auth source for all users in the organization who
          have an email address ending with the specified domain and have roles
          in the organization.
        </p>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="flex items-center gap-2">
              <Icon variant="error" icon={faWarning} size="sm" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex flex-col">
            <label className="mb-1 text-left font-medium text-gray-700">
              Target Auth Source
            </label>
            <div className="rounded border border-gray-200 bg-gray-50 p-2">
              <p className="text-gray-700 text-sm">
                {currentAuthSource.user_auth_source_name} (
                {currentAuthSource.domain_name})
              </p>
            </div>
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-left font-medium text-gray-700">
              Domain to Migrate <span className="text-red-500">*</span>
            </label>
            <Input
              errored={!!errors.domain}
              placeholder="e.g., company.com"
              suffixIcon={errors.domain ? { icon: faWarning } : undefined}
              fullWidth
              {...register("domain", {
                required: "Domain is required",
                validate: (value) => {
                  if (!value || !value.includes("."))
                    return "Please enter a valid domain name";
                  const domainRegex =
                    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
                  if (!domainRegex.test(value)) {
                    return "Please enter a valid domain name";
                  }
                  return true;
                },
              })}
            />
            {errors.domain && <ErrorHint>{errors.domain.message}</ErrorHint>}
            <p className="mt-1 font-inter-normal-12px text-gray-500">
              Users with this email domain will be migrated to the selected auth
              source
            </p>
          </div>
        </div>

        {!isValid && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-2">
            <p className="text-red-700 text-sm">
              Please fill in all required fields correctly to continue.
            </p>
          </div>
        )}
      </div>
    </ConfirmationModal>
  );
};
